import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Grid, Typography } from '@mui/material';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Table } from 'components/molecules/Table';
import { CardList } from 'components/molecules/CardList';
import { Elabels, notesTableColumns, mapNotesToDataSource } from '../../helpers';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { NotesTableCard } from './NotesTableCard';
import { EIconTypes } from 'constants/Icons';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useCase } from '../../hooks';
import { Pagination } from 'components/molecules/Pagination';
import { EPerPages, ISortOptions } from 'models/table';
import { Loader } from 'components/atoms/Loader';
import { ERouteLinks } from 'models/route';
import { FormModal } from 'components/molecules/Modal';
import { useQuery } from 'hooks/useQuery';

interface INotes {
  isFromLE: boolean;
  showAddTaskNote: boolean;
  isReadOnly?: boolean;
}

const Notes = ({ isFromLE, showAddTaskNote, isReadOnly }: INotes) => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { id = '', caseId = '' } = useParams<{ id: string; caseId: string }>();
  const query = useQuery();
  const type: string | null = query.get('type')?.trim() || null;
  const { longDateFormat, getProperties } = useGlobalProperty();
  const { isDesktop } = useThemeBreakpoint();
  const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<EPerPages>(EPerPages.perPage25);
  const { getNotesData, notes, paginatorNotes, deleteNote } = useCase();
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    (async () => {
      setIsPaginationLoading(true);
      if (!notes.length) {
        setIsLoading(true);
      }
      await getNotesData(caseId, currentPage, perPage, sort);
      setIsLoading(false);
      setIsPaginationLoading(false);
    })();
  }, [currentPage, perPage, sort]);

  const reloadCases = async () => {
    await getNotesData(caseId, currentPage, perPage, sort);
  };

  const viewAction = (noteId: string) => {
    isFromLE
      ? navigate(
          `${ERouteLinks.LegalEntityCaseNotePage.replace(':id', id)
            .replace(':caseId', caseId)
            .replace(':noteId', noteId)}?type=${type}`,
        )
      : navigate(
          ERouteLinks.CaseNotePage.replace(':caseId', caseId).replace(':noteId', noteId),
        );
  };
  const editAction = (noteId: string) => {
    isFromLE
      ? navigate(
          `${ERouteLinks.LegalEntityCaseNoteEditPage.replace(':id', id)
            .replace(':caseId', caseId)
            .replace(':noteId', noteId)}?type=${type}`,
        )
      : navigate(
          ERouteLinks.CaseNoteEditPage.replace(':caseId', caseId).replace(':noteId', noteId),
        );
  };
  const deleteAction = (id: string) => {
    setDeleteNoteId(id);
  };
  const handleDeleteNote = async () => {
    if (deleteNoteId) {
      const res = await deleteNote(caseId, deleteNoteId);
      if (res) {
        if (currentPage === 1) {
          reloadCases();
        } else {
          setCurrentPage(1);
        }
        setDeleteNoteId(null);
      }
    }
  };
  const addCase = () => {
    isFromLE
      ? navigate(
          `${ERouteLinks.LegalEntityCaseNoteAddPage.replace(':id', id).replace(':caseId', caseId)}?type=${type}`,
        )
      : navigate(ERouteLinks.CaseNoteAddPage.replace(':caseId', caseId));
  };

  return (
    <>
      <FormModal
        open={!!deleteNoteId}
        onClickClose={() => setDeleteNoteId(null)}
        onClickApprove={handleDeleteNote}
        approveButtonLabel="general.yes.label"
        cancelButtonLabel="general.no.label"
        title={t('caseManagement.cases.deleteNote.label')}
        titleLeft
        text={t('caseManagement.cases.areYouSureDeleteNote.text')}
      />
      <Grid container sx={{ gap: '20px' }}>
        <Grid
          container
          size={{ xs: 12 }}
          columnSpacing={1}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            rowGap: 1,
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            pl: 2,
          }}
        >
          <Typography variant="body2">{t(Elabels.notesText)}</Typography>
          {showAddTaskNote && (
            <Grid size={{ xs: 12, md: 'auto' }}>
              <Button
                label={t(Elabels.addNote)}
                icon={EIconTypes.plus}
                variant={EButtonVariants.contained}
                onClick={addCase}
                size={EButtonSizes.medium}
                id="notes_add-button"
                fullWidth
              />
            </Grid>
          )}
        </Grid>

        <Grid size={{ xs: 12 }}>
          {isLoading ? (
            <Loader isLoading />
          ) : (
            <>
              {isDesktop ? (
                <Table
                  columns={notesTableColumns({
                    viewAction,
                    editAction,
                    deleteAction,
                    isReadOnly,
                  })}
                  dataSource={mapNotesToDataSource(notes, longDateFormat)}
                  onSort={setSort}
                  id="notes_list-table"
                />
              ) : (
                <CardList
                  items={mapNotesToDataSource(notes, longDateFormat)}
                  render={(data, index) => (
                    <NotesTableCard
                      data={data}
                      viewAction={viewAction}
                      editAction={editAction}
                      deleteAction={deleteAction}
                      isReadOnly={isReadOnly}
                      key={index}
                    />
                  )}
                />
              )}
              {paginatorNotes && (
                <Pagination
                  count={paginatorNotes.totalElements}
                  onChangePage={(page) => setCurrentPage(page)}
                  current={currentPage}
                  perPage={perPage}
                  onChangePerPage={(value) => setPerPage(value)}
                  isLoading={isPaginationLoading}
                />
              )}
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default Notes;
